/**
 * Alert Types for PropAI Alerts Feature
 *
 * This is where we add alert types.
 *
 * This file contains TypeScript type definitions for the alerts/notifications system.
 * It includes alert types, severity mappings, and data structures for the API responses.
 */

import { logger } from "@core/logger";

/**
 * Severity classification for alerts
 */
export type AlertSeverity = "warning" | "critical";

/**
 * Add a new entry here when the API adds a new alert type.
 * - type: internal AlertType
 * - apiEnum: exact string the API sends (alert_name)
 * - severity: warning | critical
 */
const ALERT_TYPE_DEFINITIONS = [
  {
    type: "too_little_temperature_data",
    apiEnum: "TooLittleTemperatureData",
    severity: "warning",
  },
  {
    type: "inactive_radiator_system_sensor",
    apiEnum: "InactiveRadiatorSystemSensor",
    severity: "warning",
  },
  {
    type: "control_signal_problem",
    apiEnum: "ControlSignalProblem",
    severity: "critical",
  },
  {
    type: "building_control_problem",
    apiEnum: "BuildingControlProblem",
    severity: "critical",
  },
  {
    type: "building_too_cold",
    apiEnum: "BuildingTooCold",
    severity: "warning",
  },
  {
    type: "building_too_warm",
    apiEnum: "BuildingTooWarm",
    severity: "warning",
  },
] as const;

/**
 * Available alert types (derived from single source of truth)
 */
export type AlertType = (typeof ALERT_TYPE_DEFINITIONS)[number]["type"];

/**
 * Alert notification object from the API
 */
export type Alert = {
  alert_name: string;
  ends_at: string | null;
  network_uid: string | null;
  organization_uid: string | null;
  severity: string;
  starts_at: string;
  station_uid: string | null;
  status: string;
  substation_uid: string | null;
  summary: string;
  uid: string;
};

/**
 * Normalized alert interface for use in components
 * Converts API response to component-friendly format
 */
export type NormalizedAlert = {
  type: AlertType;
  uid: string;
  severity: string;
  status: string;
  starts_at: string;
  ends_at: string | null;
  summary: string;
};

/**
 * API response structure for urgent notifications endpoint
 */
export type UrgentNotificationsResponse = {
  notifications: Alert[];
  total: number;
};

/** Severity per alert type (derived) */
export const ALERT_SEVERITY_MAP: Record<AlertType, AlertSeverity> = Object.fromEntries(
  ALERT_TYPE_DEFINITIONS.map((d) => [d.type, d.severity])
) as Record<AlertType, AlertSeverity>;

/**
 * API string per internal type (derived).
 */
export const API_ALERT_TYPE_ENUM: Record<AlertType, string> = Object.fromEntries(
  ALERT_TYPE_DEFINITIONS.map((d) => [d.type, d.apiEnum])
) as Record<AlertType, string>;

/** Map API enum string to internal AlertType (derived) */
const API_TO_INTERNAL_TYPE: Record<string, AlertType> = Object.fromEntries(
  ALERT_TYPE_DEFINITIONS.map((d) => [d.apiEnum, d.type])
);

/**
 * Map API alert_name values to our internal AlertType
 */
export function mapApiAlertType(apiType: string): AlertType | null {
  return API_TO_INTERNAL_TYPE[apiType] ?? null;
}

/**
 * Transform API response to normalized format
 */
export function normalizeAlert(apiAlert: Alert): NormalizedAlert | null {
  const type = mapApiAlertType(apiAlert.alert_name);

  if (!type) {
    logger.warn(`Unknown alert type: ${apiAlert.alert_name}`);
    return null;
  }

  return {
    type,
    uid: apiAlert.uid,
    severity: apiAlert.severity,
    status: apiAlert.status,
    starts_at: apiAlert.starts_at,
    ends_at: apiAlert.ends_at,
    summary: apiAlert.summary,
  };
}

/**
 * Transform API response collection to normalized format
 */
export function normalizeAlerts(alerts: Alert[]): NormalizedAlert[] {
  return alerts.map(normalizeAlert).filter((alert): alert is NormalizedAlert => alert !== null);
}
